import React from "react";
import { Box, Typography, Paper, Grid, Alert, useTheme, useMediaQuery } from "@mui/material";
import {
  LocationOn as LocationIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  AttachMoney as MoneyIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
} from "@mui/icons-material";
import SummaryCard from "../SummaryCard";
import LocationAccordionRow from "./LocationAccordionRow";
import { formatCurrency, profitBg } from "../../utils/formatters";
import { GRADIENTS } from "../../utils/gradients";

const LocationProfitStatistics = ({ locationData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  if (!locationData) {
    return (
      <Typography variant="body1" color="text.secondary" sx={{ textAlign: "center", py: 4 }}>
        Brak danych do wyświetlenia
      </Typography>
    );
  }

  const totalRevenue = (locationData.total_single_entry_revenue || 0) + (locationData.total_pass_revenue || 0);

  return (
    <>
      {locationData.warnings && locationData.warnings.length > 0 && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
            Uwagi:
          </Typography>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {locationData.warnings.map((w, i) => (
              <li key={i}>{w}</li>
            ))}
          </ul>
        </Alert>
      )}

      {locationData.unattributed_pass_revenue > 0 && (
        <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2 }}>
          Przychód z karnetów nieprzypisany do lokalizacji:{" "}
          <strong>{formatCurrency(locationData.unattributed_pass_revenue)}</strong> — karnety wielolokalizacyjne bez
          zarejestrowanego użycia w tym okresie.
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<MoneyIcon />}
            label="Przychód łączny"
            value={formatCurrency(totalRevenue)}
            gradient={GRADIENTS.purple}
            footer={
              <Box sx={{ mt: 1 }}>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", display: "block" }}>
                  Jednorazowe: {formatCurrency(locationData.total_single_entry_revenue)} ({locationData.total_single_entry_count} wejść)
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", display: "block" }}>
                  Karnety: {formatCurrency(locationData.total_pass_revenue)} ({locationData.total_pass_attendances} wejść)
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", mt: 0.5, fontWeight: 600 }}>
                  PayU: {formatCurrency(locationData.total_paid_via_payu)}
                </Typography>
                {locationData.total_debt_cleared_amount > 0 && (
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", display: "block", pl: 1.5 }}>
                    ↳ w tym z długu: {formatCurrency(locationData.total_debt_cleared_amount)} ({locationData.total_debt_cleared_count} wejść)
                  </Typography>
                )}
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", fontWeight: 600 }}>
                  Gotówka: {formatCurrency(locationData.total_paid_cash)}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.9)", display: "block", fontWeight: 600 }}>
                  Admin: {formatCurrency(locationData.total_admin_cleared_amount)} ({locationData.total_admin_cleared_count} wejść)
                </Typography>
              </Box>
            }
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<PersonIcon />}
            label="Koszty trenerów"
            value={formatCurrency(locationData.total_trainer_cost)}
            gradient={GRADIENTS.pink}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<LocationIcon />}
            label="Koszty lokalizacji"
            value={formatCurrency(locationData.total_location_cost)}
            gradient={GRADIENTS.orange}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <SummaryCard
            icon={<TrendingUpIcon />}
            label="Zysk łączny"
            value={formatCurrency(locationData.total_profit)}
            gradient={profitBg(locationData.total_profit)}
            footer={
              locationData.unattributed_pass_revenue > 0 && (
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.75)", display: "block", mt: 0.5 }}>
                  + {formatCurrency(locationData.unattributed_pass_revenue)} nieprzypisanych
                </Typography>
              )
            }
          />
        </Grid>
      </Grid>

      {locationData.locations.length === 0 && (
        <Paper sx={{ p: 3, textAlign: "center" }}>
          <Typography color="text.secondary">Brak danych o lokalizacjach w wybranym okresie</Typography>
        </Paper>
      )}

      {locationData.locations.map((loc) => (
        <LocationAccordionRow key={loc.location_id} loc={loc} isMobile={isMobile} />
      ))}
    </>
  );
};

export default LocationProfitStatistics;
